/**
 * ToastProvider — minimal global toast surface fed by the `global.toast`
 * Pheme placement slot.
 *
 * Spec: arkheus/docs/specs/cuidari/t7-messaging-badges-banners.md §6
 * (`global.toast` is type `'toast'`, fire-and-forget, auto-dismiss).
 *
 * Implementation notes:
 *   - Mounts a portal at the bottom of the document body. Renders a stack
 *     of toasts; each auto-dismisses after `ttl_seconds` (default 5s).
 *   - Subscribes to `useMessages('global.toast')` once. Any message
 *     returned by the server that hasn't been shown yet (tracked by
 *     `message_id`) enters the visible queue.
 *   - On manual dismiss or auto-timeout, the dismiss is persisted via the
 *     hook's `dismiss(id)` so the server stops returning it.
 *
 * Kept intentionally small: no animations library, no global context API
 * for imperative `toast.show()` from arbitrary callers — toasts are
 * server-driven only, which matches T7's authoring model.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMessages } from './useMessages'
import type { ServerMessage } from './types'

const DEFAULT_TTL_MS = 5_000
const DEBUG_MARKER_RE = /(^|[^a-z0-9])debug([^a-z0-9]|$)/i

interface VisibleToast {
  id: string
  title: string
  subtitle?: string
  icon?: string
}

function includesDebugMarker(value: unknown): boolean {
  return typeof value === 'string' && DEBUG_MARKER_RE.test(value)
}

export function shouldSuppressToastInProduction(
  m: ServerMessage,
  isProduction = import.meta.env.PROD,
): boolean {
  if (!isProduction) return false

  const severity = (m as { severity?: string }).severity
  return [
    severity,
    m.title,
    m.subtitle,
    m.headline,
    m.body,
    m.decision_reason_code,
    m.content_ref,
    m.variant_id,
  ].some(includesDebugMarker)
}

export function ToastProvider() {
  const { messages, dismiss } = useMessages('global.toast')
  const [visible, setVisible] = useState<VisibleToast[]>([])
  const seenRef = useRef<Set<string>>(new Set())
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    for (const m of messages) {
      if (seenRef.current.has(m.message_id)) continue
      seenRef.current.add(m.message_id)
      if (shouldSuppressToastInProduction(m)) {
        void dismiss(m.message_id).catch(() => {})
        continue
      }
      pushToast(m)
    }
    // intentionally only watch the messages array identity — `pushToast`
    // captures the latest setVisible / dismiss closures via refs (below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages])

  // Cleanup timers on unmount.
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const t of timers.values()) clearTimeout(t)
      timers.clear()
    }
  }, [])

  function pushToast(m: ServerMessage) {
    const entry: VisibleToast = {
      id: m.message_id,
      title: m.title,
      subtitle: m.subtitle ?? undefined,
      icon: m.icon ?? undefined,
    }
    setVisible(prev => [...prev, entry])
    const ttlMs = (m.ttl_seconds && m.ttl_seconds > 0 ? m.ttl_seconds : 5) * 1000 || DEFAULT_TTL_MS
    const timer = setTimeout(() => {
      remove(m.message_id)
    }, ttlMs)
    timersRef.current.set(m.message_id, timer)
  }

  function remove(id: string) {
    setVisible(prev => prev.filter(t => t.id !== id))
    const timer = timersRef.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timersRef.current.delete(id)
    }
    void dismiss(id).catch(() => {})
  }

  if (typeof document === 'undefined') return null
  if (visible.length === 0) return null

  return createPortal(
    <div
      data-testid="toast-portal"
      // a11y: live region so toast content is announced to
      // assistive tech without stealing focus. `polite` matches
      // T7 §6 (toasts are fire-and-forget, never critical).
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
        zIndex: 1000,
        padding: '0 16px',
      }}
    >
      {visible.map(t => (
        <button
          key={t.id}
          type="button"
          data-testid={`toast-${t.id}`}
          onClick={() => remove(t.id)}
          style={{
            pointerEvents: 'auto',
            background: 'rgba(17, 17, 17, 0.92)',
            color: 'white',
            borderRadius: 14,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            maxWidth: 420,
            width: '100%',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            textAlign: 'left',
          }}
        >
          {t.icon && <span style={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>{t.icon}</span>}
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>{t.title}</span>
            {t.subtitle && (
              <span style={{ display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.78)', marginTop: 2, lineHeight: 1.4 }}>
                {t.subtitle}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>,
    document.body,
  )
}
